import React, { useState, useEffect } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import type { ChatResponseAPI } from '../types';

export type ApiMode = 'rag' | 'sql' | 'ai';

interface ChatInputProps {
  onSend: (query: string, response: ChatResponseAPI | string, mode: ApiMode) => void;
  setPendingQuery: (query: string | null) => void;
  lastQuery?: string;
  apiMode: ApiMode;
  onModeChange: (mode: ApiMode) => void;
}

const MODES: { key: ApiMode; label: string; short: string; color: string }[] = [
  { key: 'rag',  label: 'Document Search',  short: 'RAG',  color: '#60a5fa' },
  { key: 'sql',  label: 'Text → SQL',        short: 'SQL',  color: '#34d399' },
  { key: 'ai',   label: 'Under Development', short: 'AI',   color: '#f59e0b' },
];

const ChatInput: React.FC<ChatInputProps> = ({
  onSend, setPendingQuery, lastQuery, apiMode, onModeChange,
}) => {
  const [query, setQuery] = useState('');
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } =
    useSpeechRecognition();

  useEffect(() => { if (transcript) setQuery(transcript); }, [transcript]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    const q = query.trim();
    setPendingQuery(q);
    setQuery('');
    resetTranscript();

    try {
      /* ── Mode 1: RAG search (unchanged) ── */
      if (apiMode === 'rag') {
        const combinedQuery = lastQuery ? `${lastQuery} | ${q}` : q;
        const res = await fetch('https://forgerag.com/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: combinedQuery, userId: localStorage.getItem('userName')! }),
        });
        const data = await res.json();
        onSend(q, data, 'rag');

      /* ── Mode 2: Text → SQL ── */
      } else if (apiMode === 'sql') {
        const res = await fetch('https://forgerag.com/v1/generate-sql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: q }),
        });
        const sql = await res.text();
        onSend(q, sql, 'sql');

      /* ── Mode 3: Under development ── */
      } else {
        onSend(q, '🚧  This feature is under development.', 'ai');
      }
    } catch (err) {
      console.error('Chat error', err);
      setPendingQuery(null);
    }
  };

  const toggleListening = () => {
    listening ? SpeechRecognition.stopListening() : SpeechRecognition.startListening({ continuous: true });
  };

  const activeColor = MODES.find((m) => m.key === apiMode)?.color ?? '#60a5fa';

  return (
    <div>
      {/* ── Input form (layout unchanged) ── */}
      <form onSubmit={handleSubmit} className="chat-input">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            apiMode === 'sql'
              ? 'Ask a data question to generate SQL…'
              : apiMode === 'ai'
              ? 'Under development…'
              : 'Ask about your documents…'
          }
          className="inputTextArea"
          style={{ borderColor: `${activeColor}44` }}
        />
        <div className="microPhone">
          {browserSupportsSpeechRecognition && (
            <button
              type="button"
              onClick={toggleListening}
              className={`voice-btn ${listening ? 'listening' : ''}`}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', transition: 'color 0.3s ease' }}
            >
              <svg className="mic-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke={listening ? '#ff4d4d' : 'var(--mic-color)'}
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill={listening ? '#ff4d4d' : 'none'} />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
          )}
          <button type="submit"><span>↑</span></button>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
